import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withPreloading,
  PreloadAllModules,
  withRouterConfig,
  withInMemoryScrolling,
} from '@angular/router';
import { isDevMode } from '@angular/core';
import { provideHttpClient, withInterceptors, HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateLoader, TranslateCompiler } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { provideStore, provideState } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { CookieService } from 'ngx-cookie-service';
import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { authInterceptor } from './main/shared/services/auth-interceptor.service';
import { AuthService } from './main/shared/services/auth.service';
import { RaygunErrorHandler } from './main/shared/services/raygun.service';
import { rootReducer } from './main/shared/services/clientData.store';
import { ClientDataEffects } from './main/shared/services/clientData.effects';
import { SchedulerEffects } from './main/shared/services/store/scheduler/effects';
import { OrderEffects } from './main/shared/services/store/order/effects';
import { ExplorerEffects } from './main/shared/services/store/productionexplorer/effects';
import { DeviceExplorerEffects } from './main/shared/services/store/deviceexplorer/effects';
import { pendingInterceptorProviders } from '../@fury/shared/loading-indicator/pending-interceptor.provider';
import { routes } from './app.routes';
import { Ams } from './amsconfig';

const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) =>
  new TranslateHttpLoader(http, Ams.Config.BASE_URL + '/api/i18n?languageCode=', '');

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withEnabledBlockingInitialNavigation(), // or withEnabledNonBlockingInitialNavigation()
      withPreloading(PreloadAllModules),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      })
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAppInitializer(() => inject(AuthService).checkSession()),
    provideNativeDateAdapter(),
    provideMomentDateAdapter(),
    provideStore(),
    provideState({ name: 'data', reducer: rootReducer }),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
    }),
    provideEffects(
      ClientDataEffects,
      SchedulerEffects,
      OrderEffects,
      ExplorerEffects,
      DeviceExplorerEffects
    ),
    ...pendingInterceptorProviders,
    { provide: ErrorHandler, useClass: RaygunErrorHandler },
    importProvidersFrom([
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpClient],
        },
        compiler: {
          provide: TranslateCompiler,
          useClass: TranslateMessageFormatCompiler,
        },
      }),
    ]),
    provideAnimationsAsync(),
    CookieService,
    Location,
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'fill',
      },
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 5000,
        horizontalPosition: 'end',
        verticalPosition: 'bottom',
      },
    },
  ],
};
